import { DragRunArtifact } from 'src/app/model/response/ai-chat-response';

/** Geometria da faixa em unidades do viewBox. `x0` é a linha de largada; à esquerda dela fica o stage. */
export interface LaneGeometry {
  w: number;
  h: number;
  x0: number;
  x1: number;
  y: number;
}

/**
 * Linha do tempo da corrida, só com amostras que têm instante. `idx` guarda o índice original
 * de cada amostra para ler os canais a cada quadro sem repetir a filtragem.
 */
export interface Timeline {
  t: number[];
  dist: number[];
  idx: number[];
  /** Começo do replay: a primeira amostra, ou t0 quando o log só começa na largada. */
  tStart: number;
  /** Fim do replay: `run.end_s`, ou a última amostra quando o contrato não trouxe o fim. */
  tEnd: number;
  strip: number;
}

export const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/**
 * Distância `null` é lacuna do canal, não volta a zero: o carro não anda para trás. Por isso a
 * amostra sem distância herda a última conhecida.
 */
export function buildTimeline(data: DragRunArtifact): Timeline {
  const t: number[] = [];
  const dist: number[] = [];
  const idx: number[] = [];
  let lastDist = 0;

  data.series.t_s.forEach((ts, i) => {
    if (ts === null || ts === undefined) return;
    const d = data.series.dist_m[i];
    if (d !== null && d !== undefined) lastDist = d;
    t.push(ts);
    dist.push(lastDist);
    idx.push(i);
  });

  const tStart = Math.min(0, t[0] ?? 0);
  const last = t[t.length - 1] ?? 0;
  const end = data.run.end_s;
  const tEnd = Number.isFinite(end) && end > tStart ? end : last;

  return { t, dist, idx, tStart, tEnd, strip: data.run.strip_m || 1 };
}

/** Última amostra com instante <= t. Busca binária porque roda a cada quadro do replay. */
export function indexAt(tl: Timeline, t: number): number {
  const a = tl.t;
  if (!a.length) return -1;
  let lo = 0;
  let hi = a.length - 1;
  while (hi - lo > 1) {
    const m = (lo + hi) >> 1;
    if (a[m] <= t) lo = m; else hi = m;
  }
  return a[hi] <= t ? hi : lo;
}

/** Distância percorrida em t: zero antes da largada, limitada ao comprimento da pista. */
export function distanceAt(tl: Timeline, t: number): number {
  if (t < 0) return 0;
  const i = indexAt(tl, t);
  return i < 0 ? 0 : clamp(tl.dist[i], 0, tl.strip);
}

export function laneXAtDistance(m: number, tl: Timeline, g: LaneGeometry): number {
  return g.x0 + (clamp(m, 0, tl.strip) / tl.strip) * (g.x1 - g.x0);
}

/**
 * Eixo da faixa: antes de t0 é tempo, comprimido no stage à esquerda da linha de largada;
 * depois é distância. É assim que a corrida se lê — depois da largada importa onde o carro
 * estava, não quanto tempo passou.
 */
export function laneXAt(t: number, tl: Timeline, g: LaneGeometry): number {
  if (t < 0) {
    return tl.tStart === 0 ? g.x0 : ((t - tl.tStart) / (0 - tl.tStart)) * g.x0;
  }
  return laneXAtDistance(distanceAt(tl, t), tl, g);
}

/** Inverso de `laneXAt`, para o arrasto: x na faixa → instante da corrida. */
export function timeAtLaneX(x: number, tl: Timeline, g: LaneGeometry): number {
  if (x < g.x0) {
    return tl.tStart + (clamp(x, 0, g.x0) / g.x0) * (0 - tl.tStart);
  }
  const target = clamp(((x - g.x0) / (g.x1 - g.x0)) * tl.strip, 0, tl.strip);
  for (let i = 0; i < tl.t.length; i++) {
    if (tl.t[i] >= 0 && tl.dist[i] >= target) return clamp(tl.t[i], tl.tStart, tl.tEnd);
  }
  return tl.tEnd;
}

/**
 * Início do stage do 2-step, quando o payload permite saber: o evento `twostep_arm`, ou a
 * duração medida contada para trás a partir da soltura. Sem os dois, a linha não arma.
 */
export function twoStepArmStart(data: DragRunArtifact, tl: Timeline): number | null {
  const ev = data.events.find((e) => e.kind === 'twostep_arm');
  if (ev) return Math.max(tl.tStart, ev.t_s);
  if (data.two_step.available && data.two_step.duration_s) {
    return Math.max(tl.tStart, -data.two_step.duration_s);
  }
  return null;
}

/**
 * Marcha a mostrar no carro em t. Logo depois de uma troca o carro anuncia a marcha nova por
 * `hold` segundos, mesmo que o canal ainda não tenha atualizado; fora disso vale o canal.
 */
export function gearAt(data: DragRunArtifact, tl: Timeline, t: number, hold: number): number | null {
  const shift = data.events.find((e) => e.kind === 'gear' && t >= e.t_s && t < e.t_s + hold);
  if (shift?.to) return shift.to;
  const i = indexAt(tl, t);
  const g = i < 0 ? null : data.series.gear[tl.idx[i]];
  return g ?? null;
}
